import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { useT } from "@/i18n_extra";
import { useLanguage } from "@/i18n";

const MAP_EMBED =
  "https://www.google.com/maps?q=98+Foster+St,+Peabody,+MA+01960&output=embed";
const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=98+Foster+St,+Peabody,+MA+01960";

export const ContactSection = () => {
  const t = useT();
  useLanguage();
  return (
    <section id="contact" className="relative py-28 sm:py-36 bg-[#F3F1EC] border-t border-black/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-stretch">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{t.contact.label}</span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05]">{t.contact.title}</h2>
          <p className="mt-6 text-black/65 text-base sm:text-lg leading-relaxed max-w-lg">{t.contact.sub}</p>

          <ul className="mt-10 space-y-5 text-black/70 text-sm sm:text-base">
            <li className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span>98 Foster St, Peabody, MA 01960</span>
            </li>
            <li>
              <a href="tel:+18576152967" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors" data-testid="contact-phone">
                <Phone className="w-5 h-5 text-[#D4AF37] shrink-0" /> +1 857-615-2967
              </a>
            </li>
            <li>
              <a href="mailto:info@clearrouteglass.com" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors break-all" data-testid="contact-email">
                <Mail className="w-5 h-5 text-[#D4AF37] shrink-0" /> info@clearrouteglass.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span>{t.footer.weekday}: 8am – 7pm<br />{t.footer.sat}: 10am – 4pm</span>
            </li>
          </ul>

          <a
            href={DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="directions-button"
            className="mt-10 inline-flex w-fit items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:scale-105 transform"
          >
            <Navigation className="w-5 h-5" />
            {t.contact.directions}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="relative min-h-[380px]"
        >
          <div className="absolute -inset-3 border border-[#D4AF37]/40 -translate-x-4 -translate-y-4 pointer-events-none" />
          <iframe
            title="ClearRoute Auto Glass — 98 Foster St, Peabody, MA"
            src={MAP_EMBED}
            className="relative w-full h-full min-h-[380px] border border-black/10"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="map-embed"
          />
        </motion.div>
      </div>
    </section>
  );
};
